import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { MapPinned, Plus, Eye, Edit2, Trash2, Truck } from 'lucide-react';
import { DataTable } from '../../../../components/ui/DataTable';
import { ConfirmDialog } from '../../../../components/ui/ConfirmDialog';
import { PageHeader } from '../../../../components/ui/PageHeader';
import { FilterBar } from '../../../../components/ui/FilterBar';
import { SearchInput } from '../../../../components/ui/SearchInput';
import { Button } from '../../../../components/ui/Button';
import { ZoneFormModal } from './ZoneFormModal';
import {
  fetchZones,
  fetchZoneDetail,
  deleteZone,
  fetchZoneFormOptions,
} from '../../../../features/zones/zonesThunks';
import {
  setSearchFilter,
  setPage,
  setRowsPerPage,
} from '../../../../features/zones/zonesSlice';
import { formatZonePrice, getCountryLabel } from '../../../../features/zones/zonesUtils';
import { ZONE_COUNTRIES } from '../../../../features/zones/zonesConstants';
import { selectIsStaff } from '../../../../features/auth/authSelectors';

export const Zones = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { zones, totalCount, loading, saving, error, filters } = useSelector(
    (state) => state.zones
  );
  const isStaff = useSelector(selectIsStaff);

  const [searchInput, setSearchInput] = useState(filters.search);
  const [formMode, setFormMode] = useState(null);
  const [editingZoneId, setEditingZoneId] = useState(null);
  const [zoneToDelete, setZoneToDelete] = useState(null);

  // Debounce for search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchInput !== filters.search) {
        dispatch(setSearchFilter(searchInput));
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput, filters.search, dispatch]);

  // Fetch when relevant filters change
  useEffect(() => {
    dispatch(fetchZones());
  }, [dispatch, filters.search, filters.page, filters.rowsPerPage]);

  // Catalogs for the create/edit form; cached in the store after the first hit.
  useEffect(() => {
    dispatch(fetchZoneFormOptions());
  }, [dispatch]);

  /**
   * The list payload only carries the commune count, so the detail is loaded
   * before opening the form to hydrate the real coverage.
   *
   * @param {{id: number}} zone
   */
  const handleOpenEdit = (zone) => {
    dispatch(fetchZoneDetail(zone.id));
    setEditingZoneId(zone.id);
    setFormMode('edit');
  };

  const handleConfirmDelete = () => {
    dispatch(deleteZone(zoneToDelete.id))
      .unwrap()
      .then(() => {
        setZoneToDelete(null);
        dispatch(fetchZones());
      })
      .catch(() => setZoneToDelete(null));
  };

  const columns = [
    {
      // El courier va debajo del nombre: como columna propia sumaba 156 px y
      // dejaba el listado empujando la pantalla de lado.
      header: 'NOMBRE',
      cell: (row) => (
        <div className="flex flex-col items-start gap-1">
          <span className="font-bold text-ninja-dark dark:text-white">{row.name}</span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-ninja-gray dark:bg-[#2d2d2d] text-text-secondary text-xs font-semibold">
            <Truck size={13} />
            {row.courier || '-'}
          </span>
        </div>
      ),
    },
    {
      // País y comunas dicen lo mismo — dónde llega la zona —, así que van en
      // una celda de dos líneas en vez de dos columnas.
      header: 'COBERTURA',
      cell: (row) => {
        const communes = row.communes || 0;
        return (
          <div className="flex flex-col items-start gap-1">
            <span
              className={
                row.country === ZONE_COUNTRIES.MX
                  ? 'px-2.5 py-1 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-xs font-bold'
                  : 'px-2.5 py-1 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-bold'
              }
            >
              {getCountryLabel(row.country)}
            </span>
            <span className="text-xs text-text-muted">
              {communes.toLocaleString('es-CL')} comuna{communes === 1 ? '' : 's'}
            </span>
          </div>
        );
      },
    },
    {
      header: 'PRECIO',
      cell: (row) => (
        <span className="font-semibold text-ninja-dark dark:text-white">
          {formatZonePrice(row.price)}
        </span>
      ),
    },
    // Only the internal team works across admins. For everyone else this column
    // repeated their own email on every row.
    ...(isStaff
      ? [{
        header: 'ADMIN',
        cell: (row) => <span className="text-text-secondary">{row.admin_email || 'N/A'}</span>,
      }]
      : []),
    {
      header: 'ACCIONES',
      cell: (row) => (
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => navigate(`/shipping-zones/${row.id}`)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-ninja-green hover:bg-ninja-greenLight transition-colors"
            title="Ver detalle"
          >
            <Eye size={16} />
          </button>
          <button
            onClick={() => handleOpenEdit(row)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-text-muted hover:text-ninja-dark dark:hover:text-white hover:bg-ninja-gray dark:hover:bg-[#2d2d2d] transition-colors"
            title="Editar zona"
          >
            <Edit2 size={16} />
          </button>
          <button
            onClick={() => setZoneToDelete(row)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-text-muted hover:text-danger hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            title="Eliminar zona"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="space-y-6 animate-in fade-in duration-300">
        <PageHeader
          icon={MapPinned}
          iconClassName="bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400"
          title="Zonas de Despacho"
          description="La cobertura, el courier y el precio de cada zona de la plataforma."
          actions={
            <Button variant="primary" icon={Plus} onClick={() => setFormMode('create')}>
              Crear Zona
            </Button>
          }
        />

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 text-danger p-4 rounded-xl border border-red-200 dark:border-red-900 flex items-center gap-3">
            <span className="font-semibold">Error:</span> {error}
          </div>
        )}

        <FilterBar variant="bare">
          <SearchInput
            variant="bare"
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Buscar zonas por nombre, courier..."
          />
        </FilterBar>

        <DataTable
          columns={columns}
          data={zones}
          totalItems={totalCount}
          currentPage={filters.page}
          itemsPerPage={filters.rowsPerPage}
          isLoading={loading}
          itemLabel="zonas"
          onPageChange={(page) => dispatch(setPage(page))}
          onRowsPerPageChange={(rowsPerPage) => dispatch(setRowsPerPage(rowsPerPage))}
        />
      </div>

      <ZoneFormModal
        isOpen={Boolean(formMode)}
        mode={formMode || 'create'}
        zoneId={formMode === 'edit' ? editingZoneId : null}
        onClose={() => setFormMode(null)}
        onSaved={() => dispatch(fetchZones())}
      />

      <ConfirmDialog
        isOpen={Boolean(zoneToDelete)}
        onClose={() => setZoneToDelete(null)}
        onConfirm={handleConfirmDelete}
        loading={saving}
        title="Eliminar zona de despacho"
        message={`¿Seguro que quieres eliminar "${zoneToDelete?.name}"? Se perderá su cobertura y las bodegas asignadas quedarán sin esta zona.`}
      />
    </>
  );
};
